import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import { showToast } from '../components/Toast';
import ConfirmModal from '../components/ConfirmModal';
import { DSAccordion, DSAccordionItem, DSAlert, DSBadge, DSButton, DSCard, DSCheckbox, DSIcon, DSInput, DSPageHeader, DSSelect, DSSheet, DSTextarea } from '../design-system/primitives';
import ActionsMenu from '../components/ActionsMenu';
import { useEventSeriesManagement } from '../hooks/useEventSeriesManagement';
import { EVENT_SERIES_PRESETS, getPreset, applyPreset } from '../data/eventSeriesPresets';
import HostPicker from '../design-system/patterns/HostPicker';
import { parseFieldErrors, fieldState } from '../utils/formErrors';
import './EventSeriesManagement.css';

// Field paths as the API reports them, in the words the form uses.
const FIELD_LABELS = {
  name: 'Series Name',
  description: 'Description',
  html_template: 'HTML Template',
  recurrence_pattern: 'Recurrence Pattern',
  day_of_week: 'Day',
  monthly_mode: 'Monthly Pattern',
  monthly_week_of_month: 'Week of Month',
  time_of_day: 'Time of Day',
  duration_minutes: 'Duration',
  location: 'Location',
  event_category: 'Category',
  max_capacity: 'Max Capacity',
  cost_description: 'Cost Description',
  website_url: 'Website URL',
  wordpress_categories: 'WordPress Categories',
  featured_image_url: 'Featured Image URL',
  event_cost: 'Event Cost',
  season_start_date: 'Season Start Date',
  season_end_date: 'Season End Date',
  default_host_user_ids: 'Default Hosts',
  generate_advance_days: 'Generate Events Advance'
};

const fieldLabel = (path) => FIELD_LABELS[path] || path;

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// "Capital Pinball Club, 70 Pembroke Road, Concord, NH" -> "Concord, NH". The street
// address is not what distinguishes one series from another.
const shortLocation = (location) => {
  if (!location) {
    return null;
  }

  const parts = location.split(',').map((part) => part.trim()).filter(Boolean);

  return parts.length >= 2 ? parts.slice(-2).join(', ') : parts[0];
};

// "Sep 14" for a stat tile: short, and never a timestamp the reader has to parse.
const shortDate = (value) => {
  if (!value) {
    return null;
  }

  return new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

// "Tuesdays at 19:00": the one line that says what this series is.
const scheduleSummary = (seriesItem) => {
  const time = seriesItem.time_of_day ? seriesItem.time_of_day.substring(0, 5) : null;

  let when;
  if (seriesItem.recurrence_pattern === 'monthly') {
    when = 'Monthly';
  } else {
    const day = DAY_NAMES[seriesItem.day_of_week];
    const plural = day ? `${day}s` : 'Weekly';
    when = seriesItem.recurrence_pattern === 'biweekly' ? `Every other ${day || 'week'}` : plural;
  }

  return [when, time ? `at ${time}` : null].filter(Boolean).join(' ');
};

// The API returns DATE columns as ISO timestamps; <input type="date"> wants YYYY-MM-DD.
const toDateInputValue = (value) => (value ? String(value).split('T')[0] : '');

const formatSeasonDate = (value) => {
  const dateStr = toDateInputValue(value);
  if (!dateStr) {
    return null;
  }

  return new Date(dateStr + 'T00:00:00').toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

function EventSeriesManagement({ embedded = false, autoOpenCreate = false, onAutoOpenCreateHandled }) {
  const navigate = useNavigate();
  const currentUser = authService.getCurrentUser();
  const isAdmin = currentUser?.role === 'admin';

  const {
    series,
    loading,
    error,
    users,
    submitSeries,
    deleteSeries,
    generateEvents,
    toggleSeriesActive,
    unlinkedEvents,
    deleteUnlinkedEvents
  } = useEventSeriesManagement(isAdmin);

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingSeries, setEditingSeries] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    html_template: '',
    recurrence_pattern: 'weekly',
    day_of_week: 0,
    monthly_mode: 'day_of_month',
    monthly_week_of_month: 1,
    time_of_day: '19:00:00',
    duration_minutes: 180,
    location: 'Capital Pinball Club, 70 Pembroke Road, Concord, NH',
    event_category: 'other',
    max_capacity: null,
    cost_description: '$15 guest pass or $50/month membership',
    website_url: '',
    wordpress_categories: '',
    featured_image_url: '',
    event_cost: '',
    season_start_date: '',
    season_end_date: '',
    default_host_user_ids: [],
    auto_generate_enabled: true,
    generate_advance_days: 60
  });
  const [formTouched, setFormTouched] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showCleanup, setShowCleanup] = useState(false);
  const [cleanupSelection, setCleanupSelection] = useState([]);
  const [cleanupSubmitting, setCleanupSubmitting] = useState(false);
  const [presetId, setPresetId] = useState('');
  const [presetSeason, setPresetSeason] = useState({ seasonNumber: '', firstDate: '' });
  // A series is either ongoing or a fixed season. Held explicitly rather than inferred
  // from whether an end date happens to be filled in, so picking "fixed season" can show
  // the end date field before there is a date to put in it.
  const [seasonMode, setSeasonMode] = useState('ongoing');

  const [templateSource, setTemplateSource] = useState('');

  const applyTemplateFromPreset = () => {
    const preset = getPreset(templateSource);
    if (!preset?.values?.html_template) {
      return;
    }

    // The season number is only in the name of a bounded season, so read it back off the
    // series rather than making the admin type it again
    const seasonNumber = (formData.name || '').match(/Season\s+(\d+)/i)?.[1];
    const template = seasonNumber
      ? preset.values.html_template.replace(/\[Season\]/g, seasonNumber)
      : preset.values.html_template;

    updateForm((prev) => ({ ...prev, html_template: template }));
    showToast('Template replaced. Save the series to keep it.', 'success');
  };

  const handleSeasonModeChange = (nextMode) => {
    setSeasonMode(nextMode);

    if (nextMode === 'ongoing') {
      // A leftover end date would silently stop generation at the old boundary
      updateForm((prev) => ({ ...prev, season_end_date: '' }));
    }
  };
  const [submitting, setSubmitting] = useState(false);

  // The sheet can be dismissed by the overlay or Escape, so a half-filled form needs a
  // guard. Every field edit routes through setFormData, so marking dirty here covers all
  // of them without touching each onChange.
  const updateForm = (updater) => {
    setFormTouched(true);
    setFormData(updater);
  };
  const [confirmState, setConfirmState] = useState(null);
  const [confirmSubmitting, setConfirmSubmitting] = useState(false);


  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFieldErrors({});

    try {
      await submitSeries(formData, editingSeries?.id);
      setShowCreateForm(false);
      setEditingSeries(null);
      setFormTouched(false);
      setFieldErrors({});
      resetForm();
      clearPreset();
    } catch (err) {
      // The hook keeps the list-level message; the sheet shows which fields were rejected
      // so the user can fix them without reading the network tab.
      setFieldErrors(parseFieldErrors(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (seriesItem) => {
    setEditingSeries(seriesItem);
    setFormTouched(false);
    setFieldErrors({});
    clearPreset();
    setFormData({
      name: seriesItem.name,
      description: seriesItem.description || '',
      html_template: seriesItem.html_template || '',
      recurrence_pattern: seriesItem.recurrence_pattern,
      day_of_week: seriesItem.day_of_week,
      monthly_mode: seriesItem.monthly_mode || 'day_of_month',
      monthly_week_of_month: seriesItem.monthly_week_of_month || 1,
      time_of_day: seriesItem.time_of_day || '19:00:00',
      duration_minutes: seriesItem.duration_minutes || 180,
      location: seriesItem.location || 'Capital Pinball Club, 70 Pembroke Road, Concord, NH',
      event_category: seriesItem.event_category || 'other',
      max_capacity: seriesItem.max_capacity || null,
      cost_description: seriesItem.cost_description || '$15 guest pass or $50/month membership',
      website_url: seriesItem.website_url || '',
      wordpress_categories: seriesItem.wordpress_categories || '',
      featured_image_url: seriesItem.featured_image_url || '',
      event_cost: seriesItem.event_cost || '',
      season_start_date: toDateInputValue(seriesItem.season_start_date),
      season_end_date: toDateInputValue(seriesItem.season_end_date),
      default_host_user_ids: seriesItem.default_host_user_ids || [],
      auto_generate_enabled: seriesItem.auto_generate_enabled !== false,
      generate_advance_days: seriesItem.generate_advance_days || 60
    });
    setSeasonMode(seriesItem.season_end_date ? 'season' : 'ongoing');
    setShowCreateForm(true);
  };

  const handleDelete = (id) => {
    const target = series.find((item) => item.id === id);
    const upcoming = target?.upcoming_event_count;

    setConfirmState({
      title: 'Delete Event Series?',
      message: upcoming > 0
        ? `Delete this series and its ${upcoming} upcoming event${upcoming === 1 ? '' : 's'}? Past events are kept as history.`
        : 'Delete this series? Its upcoming events are removed with it; past events are kept as history.',
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => {
        try {
          setConfirmSubmitting(true);
          await deleteSeries(id);
          setConfirmState(null);
          // Deleting from inside the edit sheet leaves nothing to edit
          if (editingSeries && editingSeries.id === id) {
            closeForm();
          }
        } catch (err) {
          // Error state already set by the hook
        } finally {
          setConfirmSubmitting(false);
        }
      }
    });
  };

  const handleGenerateEvents = (id, name) => {
    setConfirmState({
      title: 'Generate Upcoming Events?',
      message: `Generate upcoming events for "${name}"?`,
      confirmLabel: 'Generate',
      onConfirm: async () => {
        try {
          setConfirmSubmitting(true);
          const eventsCreated = await generateEvents(id);
          showToast(`Successfully generated ${eventsCreated} events`, 'success');
          setConfirmState(null);
        } catch (err) {
          // Error state already set by the hook
        } finally {
          setConfirmSubmitting(false);
        }
      }
    });
  };

  const handleToggleActive = async (id, currentStatus) => {
    try {
      await toggleSeriesActive(id, currentStatus);
    } catch (err) {
      // Error state already set by the hook
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      html_template: '',
      recurrence_pattern: 'weekly',
      day_of_week: 0,
      monthly_mode: 'day_of_month',
      monthly_week_of_month: 1,
      time_of_day: '19:00:00',
      duration_minutes: 180,
      location: 'Capital Pinball Club, 70 Pembroke Road, Concord, NH',
      event_category: 'other',
      max_capacity: null,
      cost_description: '$15 guest pass or $50/month membership',
      website_url: '',
      wordpress_categories: '',
      featured_image_url: '',
      event_cost: '',
      season_start_date: '',
      season_end_date: '',
      default_host_user_ids: [],
      auto_generate_enabled: true,
      generate_advance_days: 60
    });
    setSeasonMode('ongoing');
  };

  const closeForm = () => {
    setShowCreateForm(false);
    setEditingSeries(null);
    setFormTouched(false);
    setFieldErrors({});
    resetForm();
    clearPreset();
  };

  const handleCancel = () => {
    if (!formTouched) {
      closeForm();
      return;
    }

    setConfirmState({
      title: editingSeries ? 'Discard Changes?' : 'Discard This Series?',
      message: editingSeries
        ? 'Your edits to this series have not been saved. Close without saving?'
        : 'This series has not been created yet. Close without saving?',
      confirmLabel: 'Discard',
      danger: true,
      onConfirm: () => {
        setConfirmState(null);
        closeForm();
      }
    });
  };

  const clearPreset = () => {
    setPresetId('');
    setPresetSeason({ seasonNumber: '', firstDate: '' });
  };

  // Presets prefill; they never lock a field. Re-applying on every answer change
  // keeps the name and season window in step with the season number and first date.
  const applyPresetToForm = (nextPresetId, nextSeason) => {
    const preset = getPreset(nextPresetId);
    if (!preset) {
      return;
    }

    const applied = applyPreset(preset, nextSeason);
    updateForm((prev) => ({ ...prev, ...applied }));
    // NEPL brings a season window; Queer Pinball and the other ongoing ones clear it
    setSeasonMode(preset.needsSeason ? 'season' : 'ongoing');
  };

  const handlePresetChange = (nextPresetId) => {
    setPresetId(nextPresetId);

    if (!nextPresetId) {
      return;
    }

    applyPresetToForm(nextPresetId, presetSeason);
  };

  const handlePresetSeasonChange = (field, value) => {
    const nextSeason = { ...presetSeason, [field]: value };
    setPresetSeason(nextSeason);
    applyPresetToForm(presetId, nextSeason);
  };

  const openCleanup = () => {
    setCleanupSelection(unlinkedEvents.map((event) => event.id));
    setShowCleanup(true);
  };

  const toggleCleanupEvent = (eventId) => {
    setCleanupSelection((prev) => (
      prev.includes(eventId) ? prev.filter((id) => id !== eventId) : [...prev, eventId]
    ));
  };

  const handleCleanupDelete = async () => {
    try {
      setCleanupSubmitting(true);
      const result = await deleteUnlinkedEvents(cleanupSelection);
      showToast(result.message || 'Events deleted', 'success');
      setShowCleanup(false);
      setCleanupSelection([]);
    } catch (err) {
      showToast('Failed to delete events: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setCleanupSubmitting(false);
    }
  };

  const isSeasonOver = (seriesItem) => {
    const endDate = toDateInputValue(seriesItem.season_end_date);
    if (!endDate) {
      return false;
    }

    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    return endDate < todayStr;
  };

  // The hub's create sheet can route straight here; open the form once and hand the flag back.
  React.useEffect(() => {
    if (autoOpenCreate) {
      setShowCreateForm(true);
      if (onAutoOpenCreateHandled) {
        onAutoOpenCreateHandled();
      }
    }
  }, [autoOpenCreate, onAutoOpenCreateHandled]);

  const getDayName = (dayNumber) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[dayNumber] || dayNumber;
  };

  const getRecurrenceLabel = (pattern) => {
    const labels = {
      'weekly': 'Weekly',
      'biweekly': 'Bi-weekly',
      'monthly': 'Monthly'
    };
    return labels[pattern] || pattern;
  };

  const getMonthlyScheduleLabel = (seriesItem) => {
    if ((seriesItem.monthly_mode || 'day_of_month') === 'nth_weekday') {
      const weekLabels = {
        1: 'first',
        2: 'second',
        3: 'third',
        4: 'fourth',
        '-1': 'last'
      };
      const weekLabel = weekLabels[seriesItem.monthly_week_of_month] || `${seriesItem.monthly_week_of_month}th`;
      return `${weekLabel} ${getDayName(seriesItem.day_of_week)}`;
    }

    return `day ${seriesItem.day_of_week}`;
  };

  if (!isAdmin) {
    return (
      <div className="event-series-management">
        <div className="error">You must be an admin to access this page.</div>
      </div>
    );
  }

  return (
    <div className={`event-series-management${embedded ? ' embedded' : ''}`}>
      {/* Embedded in the Events hub, the hub header owns the single Create control; a
          second one here was the duplicate button in the admin tab. */}
      {!embedded && (
        <DSPageHeader
          title="Event Series Management"
          actions={!showCreateForm && (
            <DSButton appearance="solid" tone="primary" onClick={() => setShowCreateForm(true)}>
              + Create Series
            </DSButton>
          )}
        />
      )}

      {error && <div className="error">{error}</div>}

      {/* Deleting a series used to leave its events behind, and events.series_id is
          ON DELETE SET NULL, so those orphans are indistinguishable from hand-made
          events. New deletes take their upcoming events with them; this clears out
          whatever the old behaviour left lying around. */}
      {unlinkedEvents.length > 0 && (
        <DSAlert variant="warning">
          <div className="series-cleanup">
            <span>
              <strong>{unlinkedEvents.length} upcoming event{unlinkedEvents.length === 1 ? '' : 's'} not linked to a series.</strong>{' '}
              Left behind by a deleted series, or created by hand.
            </span>
            <DSButton appearance="outline" tone="neutral" size="sm" onClick={openCleanup}>
              Review
            </DSButton>
          </div>
        </DSAlert>
      )}

      {showCleanup && (
        <DSSheet
          title="Unlinked upcoming events"
          onClose={() => setShowCleanup(false)}
          footer={(
            <>
              <DSButton
                type="button"
                appearance="outline"
                tone="neutral"
                onClick={() => setShowCleanup(false)}
                disabled={cleanupSubmitting}
              >
                Cancel
              </DSButton>
              <DSButton
                type="button"
                appearance="solid"
                tone="danger"
                onClick={handleCleanupDelete}
                disabled={cleanupSubmitting || cleanupSelection.length === 0}
              >
                {cleanupSubmitting ? 'Deleting...' : `Delete ${cleanupSelection.length} event${cleanupSelection.length === 1 ? '' : 's'}`}
              </DSButton>
            </>
          )}
        >
          <p className="cleanup-intro">
            These have no series behind them. Anything with RSVPs is worth a second look
            before removing it.
          </p>

          <div className="cleanup-toolbar">
            <DSButton
              appearance="text"
              tone="secondary"
              size="sm"
              onClick={() => setCleanupSelection(
                cleanupSelection.length === unlinkedEvents.length ? [] : unlinkedEvents.map((e) => e.id)
              )}
            >
              {cleanupSelection.length === unlinkedEvents.length ? 'Select none' : 'Select all'}
            </DSButton>
          </div>

          <div className="cleanup-list">
            {unlinkedEvents.map((event) => (
              <label key={event.id} className="cleanup-row">
                <DSCheckbox
                  id={`cleanup-${event.id}`}
                  checked={cleanupSelection.includes(event.id)}
                  onChange={() => toggleCleanupEvent(event.id)}
                />
                <span className="cleanup-row__main">
                  <span className="cleanup-row__title">{event.title}</span>
                  <span className="cleanup-row__detail">
                    {new Date(event.event_date).toLocaleString(undefined, {
                      weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
                    })}
                    {event.rsvp_count > 0 && ` \u00b7 ${event.rsvp_count} RSVP${event.rsvp_count === 1 ? '' : 's'}`}
                  </span>
                </span>
              </label>
            ))}
          </div>
        </DSSheet>
      )}

      {showCreateForm && (
        <DSSheet
          title={editingSeries ? 'Edit Series' : 'Create New Series'}
          onClose={handleCancel}
          footer={(
            <>
              {editingSeries && (
                <DSButton
                  type="button"
                  appearance="text"
                  tone="danger"
                  className="series-sheet__delete"
                  onClick={() => handleDelete(editingSeries.id)}
                  disabled={submitting}
                >
                  Delete Series
                </DSButton>
              )}
              <DSButton type="button" appearance="outline" tone="neutral" onClick={handleCancel} disabled={submitting}>
                Cancel
              </DSButton>
              <DSButton type="submit" form="event-series-form" appearance="solid" tone="primary" disabled={submitting}>
                {submitting ? 'Saving...' : (editingSeries ? 'Update Series' : 'Create Series')}
              </DSButton>
            </>
          )}
        >
          {editingSeries && (
            <div className="series-sheet__actions">
              <DSButton
                type="button"
                appearance="outline"
                tone="secondary"
                size="sm"
                onClick={() => navigate(`/event-series/${editingSeries.id}/dates`)}
              >
                Manage dates
              </DSButton>
              <DSButton
                type="button"
                appearance="outline"
                tone="neutral"
                size="sm"
                onClick={() => handleToggleActive(editingSeries.id, editingSeries.is_active)}
              >
                {editingSeries.is_active ? 'Deactivate Series' : 'Activate Series'}
              </DSButton>
            </div>
          )}

          {Object.keys(fieldErrors).length > 0 && (
            <DSAlert variant="error">
              <strong>This series could not be saved.</strong>
              <ul className="series-form__errors">
                {Object.entries(fieldErrors).map(([path, message]) => (
                  <li key={path}>{fieldLabel(path)}: {message}</li>
                ))}
              </ul>
            </DSAlert>
          )}

          {!editingSeries && (
            <div className="series-preset">
              <div className="form-group">
                <DSSelect
                  label="Start From a Preset"
                  value={presetId}
                  onChange={(e) => handlePresetChange(e.target.value)}
                >
                  <option value="">Start from scratch</option>
                  {EVENT_SERIES_PRESETS.map((preset) => (
                    <option key={preset.id} value={preset.id}>{preset.label}</option>
                  ))}
                </DSSelect>
                <p className="field-help">
                  {getPreset(presetId)?.summary || 'Fills in everything we already know about a recurring event. Every field stays editable.'}
                </p>
              </div>

              {getPreset(presetId)?.needsSeason && (
                <div className="form-row">
                  <div className="form-group">
                    <DSInput
                      label="Season Number"
                      type="number"
                      min="1"
                      value={presetSeason.seasonNumber}
                      onChange={(e) => handlePresetSeasonChange('seasonNumber', e.target.value)}
                      placeholder="e.g., 38"
                    />
                  </div>

                  <div className="form-group">
                    <DSInput
                      label="First Night"
                      type="date"
                      value={presetSeason.firstDate}
                      onChange={(e) => handlePresetSeasonChange('firstDate', e.target.value)}
                      helpText={`Sets the season window automatically (${getPreset(presetId)?.seasonWeeks} weeks).`}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          <form id="event-series-form" onSubmit={handleSubmit} className="series-form">
            <div className="form-row">
              <div className="form-group">
                <DSInput
                  label="Series Name *"
                  state={fieldState(fieldErrors, 'name')}
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={(e) => updateForm({ ...formData, name: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <DSTextarea
                label="Description (Plain Text Summary)"
                name="description"
                value={formData.description}
                onChange={(e) => updateForm({ ...formData, description: e.target.value })}
                rows="2"
                placeholder="Brief description for internal use"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <DSSelect
                  label="Recurrence Pattern *"
                  name="recurrence_pattern"
                  value={formData.recurrence_pattern}
                  onChange={(e) => {
                    const nextPattern = e.target.value;
                    updateForm((prev) => ({
                      ...prev,
                      recurrence_pattern: nextPattern,
                      day_of_week: nextPattern === 'monthly'
                        ? ((prev.monthly_mode || 'day_of_month') === 'nth_weekday' ? Math.min(Math.max(prev.day_of_week ?? 1, 0), 6) : Math.min(Math.max(prev.day_of_week || 1, 1), 31))
                        : Math.min(Math.max(prev.day_of_week ?? 0, 0), 6)
                    }));
                  }}
                  required
                >
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Bi-weekly</option>
                  <option value="monthly">Monthly</option>
                </DSSelect>
              </div>

              <div className="form-group">
                {formData.recurrence_pattern === 'monthly' ? (
                  <DSSelect
                    label="Monthly Pattern *"
                    name="monthly_mode"
                    value={formData.monthly_mode || 'day_of_month'}
                    onChange={(e) => {
                      const nextMonthlyMode = e.target.value;
                      updateForm((prev) => ({
                        ...prev,
                        monthly_mode: nextMonthlyMode,
                        day_of_week: nextMonthlyMode === 'nth_weekday'
                          ? Math.min(Math.max(prev.day_of_week ?? 1, 0), 6)
                          : Math.min(Math.max(prev.day_of_week || 1, 1), 31)
                      }));
                    }}
                    required
                  >
                    <option value="day_of_month">Specific day of month</option>
                    <option value="nth_weekday">Nth weekday of month</option>
                  </DSSelect>
                ) : (
                  <DSSelect
                    label="Day of Week *"
                    name="day_of_week"
                    value={formData.day_of_week}
                    onChange={(e) => updateForm({ ...formData, day_of_week: parseInt(e.target.value, 10) })}
                    required
                  >
                    <option value="0">Sunday</option>
                    <option value="1">Monday</option>
                    <option value="2">Tuesday</option>
                    <option value="3">Wednesday</option>
                    <option value="4">Thursday</option>
                    <option value="5">Friday</option>
                    <option value="6">Saturday</option>
                  </DSSelect>
                )}
              </div>
            </div>

            {formData.recurrence_pattern === 'monthly' && (
              <div className="form-row">
                {(formData.monthly_mode || 'day_of_month') === 'day_of_month' ? (
                  <div className="form-group">
                    <DSInput
                      label="Day of Month *"
                      type="number"
                      min="1"
                      max="31"
                      value={formData.day_of_week}
                      onChange={(e) => updateForm({ ...formData, day_of_week: parseInt(e.target.value, 10) })}
                      required
                    />
                  </div>
                ) : (
                  <>
                    <div className="form-group">
                      <DSSelect
                        label="Week of Month *"
                        value={formData.monthly_week_of_month}
                        onChange={(e) => updateForm({ ...formData, monthly_week_of_month: parseInt(e.target.value, 10) })}
                        required
                      >
                        <option value="1">First</option>
                        <option value="2">Second</option>
                        <option value="3">Third</option>
                        <option value="4">Fourth</option>
                        <option value="-1">Last</option>
                      </DSSelect>
                    </div>
                    <div className="form-group">
                      <DSSelect
                        label="Day of Week *"
                        value={formData.day_of_week}
                        onChange={(e) => updateForm({ ...formData, day_of_week: parseInt(e.target.value, 10) })}
                        required
                      >
                        <option value="0">Sunday</option>
                        <option value="1">Monday</option>
                        <option value="2">Tuesday</option>
                        <option value="3">Wednesday</option>
                        <option value="4">Thursday</option>
                        <option value="5">Friday</option>
                        <option value="6">Saturday</option>
                      </DSSelect>
                    </div>
                  </>
                )}
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <DSInput
                  label="Time of Day *"
                  state={fieldState(fieldErrors, 'time_of_day')}
                  type="time"
                  value={formData.time_of_day ? formData.time_of_day.substring(0, 5) : '19:00'}
                  onChange={(e) => updateForm({ ...formData, time_of_day: e.target.value + ':00' })}
                  required
                />
              </div>

              <div className="form-group">
                <DSInput
                  label="Duration (minutes)"
                  state={fieldState(fieldErrors, 'duration_minutes')}
                  type="number"
                  min="30"
                  max="1440"
                  value={formData.duration_minutes}
                  onChange={(e) => updateForm({ ...formData, duration_minutes: parseInt(e.target.value) })}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <DSSelect
                  label="Category"
                  name="event_category"
                  value={formData.event_category}
                  onChange={(e) => updateForm({ ...formData, event_category: e.target.value })}
                >
                  <option value="pinball">Pinball</option>
                  <option value="video_game">Video Game</option>
                  <option value="board_game">Board Game</option>
                  <option value="book_club">Book Club</option>
                  <option value="other">Other</option>
                </DSSelect>
              </div>

              <div className="form-group">
                <DSInput
                  label="Cost Description"
                  type="text"
                  name="cost_description"
                  value={formData.cost_description}
                  onChange={(e) => updateForm({ ...formData, cost_description: e.target.value })}
                  placeholder="e.g., $15 guest pass or $50/month membership"
                />
              </div>
            </div>

            {/* Two bare optional date fields made "runs forever" look like a field you
                forgot rather than a choice you made, and a half-filled window (a start
                with no end) reads as broken input when it is actually valid. Asking which
                kind of series it is first gives the dates below a single meaning. */}
            <div className="form-group">
              <DSSelect
                label="How long does it run?"
                value={seasonMode}
                onChange={(e) => handleSeasonModeChange(e.target.value)}
                helpText={seasonMode === 'ongoing'
                  ? 'Keeps generating, like board game night. Nothing to renew each season.'
                  : 'No events are generated outside the season window.'}
              >
                <option value="ongoing">Ongoing - runs until we stop it</option>
                <option value="season">Fixed season - runs between two dates</option>
              </DSSelect>
            </div>

            <div className="form-row">
              <div className="form-group">
                <DSInput
                  label={seasonMode === 'season' ? 'Season Start Date' : 'First Date (optional)'}
                  state={fieldState(fieldErrors, 'season_start_date')}
                  type="date"
                  value={formData.season_start_date}
                  onChange={(e) => updateForm({ ...formData, season_start_date: e.target.value })}
                  helpText={seasonMode === 'season'
                    ? 'The season starts here.'
                    : 'Leave blank to start from the next matching date.'}
                />
              </div>

              {seasonMode === 'season' && (
                <div className="form-group">
                  <DSInput
                    label="Season End Date"
                    state={fieldState(fieldErrors, 'season_end_date')}
                    type="date"
                    value={formData.season_end_date}
                    min={formData.season_start_date || undefined}
                    onChange={(e) => updateForm({ ...formData, season_end_date: e.target.value })}
                    helpText="The last date the series can generate."
                  />
                </div>
              )}
            </div>

            <div className="form-group">
              <HostPicker
                id="series-default-hosts"
                label="Default Hosts"
                multiple
                hosts={users}
                value={formData.default_host_user_ids}
                onChange={(ids) => updateForm({ ...formData, default_host_user_ids: ids })}
                helpText="Assigned automatically to every generated event, for series run by the same hosts all season."
              />
            </div>

            <div className="form-group">
              <DSInput
                label="Max Capacity"
                  state={fieldState(fieldErrors, 'max_capacity')}
                type="number"
                min="1"
                value={formData.max_capacity || ''}
                onChange={(e) => updateForm({ ...formData, max_capacity: e.target.value ? parseInt(e.target.value) : null })}
                placeholder="No limit"
              />
            </div>

            <DSAccordion>
              <DSAccordionItem title="Website & advanced options">

              <div className="form-group">
                <DSTextarea
                  label="HTML Template for WordPress"
                  name="html_template"
                  value={formData.html_template}
                  onChange={(e) => updateForm({ ...formData, html_template: e.target.value })}
                  rows="6"
                  placeholder="HTML template with [Host Name] and [Game Name] placeholders..."
                  className="series-template-input"
                  helpText="Use [Host Name] and [Game Name] as placeholders. This will be used for WordPress event description."
                />
              </div>

              {/* A series keeps its own copy of the template from the day it was created,
                  so fixing the wording in a preset does nothing for the series already
                  running on it. Without this, every copy change meant a SQL script against
                  the database, which is not a thing a host should ever need. */}
              <div className="form-group series-template-refresh">
                <DSSelect
                  label="Replace the template from a preset"
                  value={templateSource}
                  onChange={(e) => setTemplateSource(e.target.value)}
                  helpText="Overwrites the box above. Nothing is saved until you save the series."
                >
                  <option value="">Choose a preset...</option>
                  {EVENT_SERIES_PRESETS.map((preset) => (
                    <option key={preset.id} value={preset.id}>{preset.label}</option>
                  ))}
                </DSSelect>
                <DSButton
                  appearance="outline"
                  tone="neutral"
                  disabled={!templateSource}
                  onClick={applyTemplateFromPreset}
                >
                  Use this preset's template
                </DSButton>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <DSInput
                    label="Website URL"
                  state={fieldState(fieldErrors, 'website_url')}
                    type="url"
                    name="website_url"
                    value={formData.website_url}
                    onChange={(e) => updateForm({ ...formData, website_url: e.target.value })}
                    placeholder="https://capitalpinballclub.com/event/..."
                    helpText="External URL to reference for this event series"
                  />
                </div>

                <div className="form-group">
                  <DSInput
                    label="WordPress Categories"
                    type="text"
                    name="wordpress_categories"
                    value={formData.wordpress_categories}
                    onChange={(e) => updateForm({ ...formData, wordpress_categories: e.target.value })}
                    placeholder="e.g., Board Games, Open Gaming"
                    helpText="Comma-separated categories for WordPress"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <DSInput
                    label="Featured Image URL"
                  state={fieldState(fieldErrors, 'featured_image_url')}
                    type="url"
                    name="featured_image_url"
                    value={formData.featured_image_url}
                    onChange={(e) => updateForm({ ...formData, featured_image_url: e.target.value })}
                    placeholder="https://example.com/image.jpg"
                    helpText="Featured image URL for WordPress events"
                  />
                </div>

                <div className="form-group">
                  <DSInput
                    label="Event Cost"
                  state={fieldState(fieldErrors, 'event_cost')}
                    type="text"
                    name="event_cost"
                    value={formData.event_cost}
                    onChange={(e) => updateForm({ ...formData, event_cost: e.target.value })}
                    placeholder="e.g., 30, Free, $5-10"
                    helpText="Event cost for WordPress (numeric or text)"
                  />
                </div>
              </div>

              <div className="form-group">
                <DSInput
                  label="Generate Events Advance (days)"
                  type="number"
                  min="7"
                  max="365"
                  value={formData.generate_advance_days}
                  onChange={(e) => updateForm({ ...formData, generate_advance_days: parseInt(e.target.value) })}
                  helpText="How far ahead events are created. A season window still caps generation."
                />
              </div>
              </DSAccordionItem>
            </DSAccordion>

            <div className="form-group">
              <DSCheckbox
                id="auto-generate-enabled"
                label="Enable automatic event generation"
                checked={formData.auto_generate_enabled}
                onChange={(e) => updateForm({ ...formData, auto_generate_enabled: e.target.checked })}
              />
            </div>

          </form>
        </DSSheet>
      )}

      {loading ? (
        <div className="loading">Loading event series...</div>
      ) : (
        <div className="series-list">
          {series.length === 0 ? (
            <div className="no-series">
              No event series created yet. Click "Create Series" to get started.
            </div>
          ) : (
            series.map((item) => (
              <DSCard
                key={item.id}
                cornerVariant="md"
                bgVariant={!item.is_active ? 'subtle' : 'default'}
                className={`series-card ${!item.is_active ? 'inactive' : ''}`}
              >
                <div className="series-card__head">
                  <h3 className="series-card__name">{item.name}</h3>
                  {item.is_active ? (
                    <DSBadge variant="success" size="sm" uppercase>Active</DSBadge>
                  ) : (
                    <DSBadge variant="info" appearance="outline" size="sm" uppercase>Paused</DSBadge>
                  )}
                </div>

                <p className="series-card__schedule">
                  {[scheduleSummary(item), shortLocation(item.location)].filter(Boolean).join(' \u00b7 ')}
                </p>

                {/* The three facts you open this screen to check. Three across at every
                    width - stacked, they pushed the actions off the first screen. */}
                <div className="series-card__stats">
                  <div className="series-stat">
                    <span className="series-stat__label">Events made</span>
                    <span className="series-stat__value">{item.event_count || 0}</span>
                  </div>
                  <div className="series-stat">
                    <span className="series-stat__label">Next night</span>
                    <span className="series-stat__value">
                      {shortDate(item.next_event_date) || (isSeasonOver(item) ? 'Season over' : 'None')}
                    </span>
                  </div>
                  <div className="series-stat">
                    <span className="series-stat__label">Auto-gen</span>
                    <span className={`series-stat__value${item.auto_generate_enabled ? '' : ' series-stat__value--muted'}`}>
                      {item.auto_generate_enabled ? 'On' : 'Paused'}
                    </span>
                  </div>
                </div>

                {/* Two things you do often, and everything else behind one control. */}
                <div className="series-actions">
                  <DSButton
                    appearance="solid"
                    tone="primary"
                    onClick={() => handleGenerateEvents(item.id, item.name)}
                  >
                    Generate events
                  </DSButton>
                  <DSButton
                    appearance="outline"
                    tone="neutral"
                    onClick={() => navigate(`/event-series/${item.id}/dates`)}
                  >
                    Manage {item.upcoming_event_count ?? item.event_count ?? 0}
                  </DSButton>
                  <ActionsMenu
                    compact
                    label={`More actions for ${item.name}`}
                    actions={[
                      { label: 'Edit series', onClick: () => handleEdit(item) },
                      {
                        label: item.is_active ? 'Pause series' : 'Activate series',
                        onClick: () => handleToggleActive(item.id, item.is_active)
                      },
                      { label: 'Delete series', onClick: () => handleDelete(item.id), tone: 'danger' }
                    ]}
                  />
                </div>

                {/* Reference, not something you scan. Eight labelled fields on every card
                    is what made this list a scroll. */}
                <details className="series-card__more">
                  <summary className="series-card__more-summary">
                    <span className="series-card__more-show">Series settings and description</span>
                    <span className="series-card__more-hide">Hide series settings</span>
                    <DSIcon name="chevronDown" size="sm" tone="brand" />
                  </summary>

                  {item.description && (
                    <p className="series-description">{item.description}</p>
                  )}

                  <dl className="series-facts">
                    <div className="series-fact">
                      <dt>Schedule</dt>
                      <dd>
                        {getRecurrenceLabel(item.recurrence_pattern)} on{' '}
                        {item.recurrence_pattern === 'monthly'
                          ? getMonthlyScheduleLabel(item)
                          : getDayName(item.day_of_week)
                        }{' '}
                        at {item.time_of_day?.substring(0, 5) || 'N/A'}
                      </dd>
                    </div>
                    <div className="series-fact">
                      <dt>Location</dt>
                      <dd>{item.location || 'Not specified'}</dd>
                    </div>
                    <div className="series-fact">
                      <dt>Category</dt>
                      <dd className="series-fact__capitalize">{item.event_category}</dd>
                    </div>
                    <div className="series-fact">
                      <dt>Max capacity</dt>
                      <dd>{item.max_capacity || 'Unlimited'}</dd>
                    </div>
                    <div className="series-fact">
                      <dt>Season</dt>
                      <dd>
                        {item.season_start_date || item.season_end_date ? (
                          <>
                            {formatSeasonDate(item.season_start_date) || 'Open start'} to{' '}
                            {formatSeasonDate(item.season_end_date) || 'Open end'}
                            {isSeasonOver(item) && ' (ended)'}
                          </>
                        ) : 'Ongoing'}
                      </dd>
                    </div>
                    <div className="series-fact">
                      <dt>Default hosts</dt>
                      <dd>{item.default_host_names?.length > 0 ? item.default_host_names.join(', ') : 'None'}</dd>
                    </div>
                    <div className="series-fact">
                      <dt>Auto-generate</dt>
                      <dd>{item.auto_generate_enabled ? `Yes, ${item.generate_advance_days} days ahead` : 'No'}</dd>
                    </div>
                    <div className="series-fact">
                      <dt>Blackout dates</dt>
                      <dd>{item.blackout_count || 'None'}</dd>
                    </div>
                  </dl>
                </details>
              </DSCard>
            ))
          )}

          {/* The list's own way to add to it, so the header keeps one Create control */}
          <button type="button" className="series-new" onClick={() => setShowCreateForm(true)}>
            + New series
          </button>
        </div>
      )}


      {confirmState && (
        <ConfirmModal
          title={confirmState.title}
          message={confirmState.message}
          confirmLabel={confirmState.confirmLabel}
          danger={confirmState.danger}
          submitting={confirmSubmitting}
          onConfirm={confirmState.onConfirm}
          onCancel={() => setConfirmState(null)}
        />
      )}

    </div>
  );
}

export default EventSeriesManagement;
